import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Input, InputWrapper, Label, ErrorMessage } from '../components/Input';
import { isValidEmail } from '../utils/validation';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { AnimatedLogo } from '../components/AnimatedLogo';
import { ParticleField } from '../components/ParticleField';
import { ThemeToggle } from '../components/ThemeToggle';

const Page = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const FormCard = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xxl};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Footer = styled.p`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const CoopenButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: ${({ theme }) => theme.borders.thin};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  transition: background 0.15s ease;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

export function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coopenAuthUrl, setCoopenAuthUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<{ authUrl: string; enabled: boolean }>('/api/auth/coopen-config')
      .then((cfg) => { if (!cancelled && cfg.enabled) setCoopenAuthUrl(cfg.authUrl); })
      .catch(() => { /* SSO no disponible — seguimos con email/password */ });
    return () => { cancelled = true; };
  }, []);

  const handleCoopenLogin = () => {
    if (!coopenAuthUrl) return;
    const returnTo = `${window.location.origin}/auth/coopen-callback`;
    const url = new URL('/auth/google/authorize', coopenAuthUrl);
    url.searchParams.set('return_to', returnTo);
    window.location.href = url.toString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Validación inline previa al submit (consistente con Register): feedback
    // claro sin un round-trip al server para errores obvios.
    if (!isValidEmail(email)) { setError('Enter a valid email address'); return; }
    if (!password) { setError('Enter your password'); return; }
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page>
      <ParticleField density={0.9} />
      <ThemeToggle floating />
      <FormCard>
        <Logo>
          <AnimatedLogo variant="v1" tone="auto" height={40} />
        </Logo>
        <Title>Welcome back</Title>
        <Subtitle>Sign in to your account</Subtitle>
        {coopenAuthUrl && (
          <>
            <CoopenButton type="button" onClick={handleCoopenLogin}>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.12A6.79 6.79 0 0 1 5.5 12c0-.74.13-1.46.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.42 3.46 1.18 4.96l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
              </svg>
              Continue with Coopen
            </CoopenButton>
            <Divider>or with your email</Divider>
          </>
        )}
        <Form onSubmit={handleSubmit}>
          <InputWrapper>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </InputWrapper>
          <InputWrapper>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
            />
          </InputWrapper>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" $fullWidth disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </Form>
        <GoogleSignInButton onSuccess={loginWithGoogle} />
        <Footer>
          <Link to="/forgot-password">Forgot your password?</Link>
        </Footer>
        <Footer>
          Don&apos;t have an account? <Link to="/register">Sign up</Link>
        </Footer>
      </FormCard>
    </Page>
  );
}
